import React, { useState } from 'react';
import { 
  Box, 
  Text, 
  Code, 
  Anchor,
  Stack,
  List,
  Title,
  Paper,
  Collapse,
  Button,
  Group,
} from '@mantine/core';
import { IconExternalLink, IconChevronDown, IconChevronRight, IconBook } from '@tabler/icons';

interface Instruction {
  type: 'text' | 'steps' | 'code' | 'link';
  title?: string;
  content: string;
  language?: string; // for code blocks
  url?: string; // for links
}

interface InstructionsProps {
  instructions: Instruction[];
  title?: string;
}

const InstructionItem: React.FC<{ instruction: Instruction }> = ({ instruction }) => {
  switch (instruction.type) {
  case 'text':
    return (
      <Box>
        {instruction.title && (
          <Text weight={500} size="sm" mb="xs" color="dark">
            {instruction.title}
          </Text>
        )}
        <Text size="sm" color="dimmed">
          {instruction.content}
        </Text>
      </Box>
    );

  case 'steps':
    return (
      <Box>
        {instruction.title && (
          <Text weight={500} size="sm" mb="xs" color="dark">
            {instruction.title}
          </Text>
        )}
        <List size="sm" spacing="xs" type="ordered">
          {instruction.content.split('\n').map((step, index) => {
            // Remove the number prefix if it exists (e.g., "1. Step" -> "Step")
            const cleanStep = step.replace(/^\d+\.\s*/, '');
            return (
              <List.Item key={index}>
                <Text size="sm" color="dimmed">
                  {cleanStep}
                </Text>
              </List.Item>
            );
          })}
        </List>
      </Box>
    );

  case 'code':
    return (
      <Box>
        {instruction.title && (
          <Text weight={500} size="sm" mb="xs" color="dark">
            {instruction.title}
          </Text>
        )}
        <Code block lang={instruction.language || 'json'}>
          {instruction.content}
        </Code>
      </Box>
    );

  case 'link':
    return (
      <Box>
        <Anchor
          href={instruction.url}
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
        >
          <Text size="sm" color="blue" sx={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {instruction.content}
            <IconExternalLink size={12} />
          </Text>
        </Anchor>
      </Box>
    );

  default:
    return null;
  }
};

export const Instructions: React.FC<InstructionsProps> = ({ instructions, title = "Setup Instructions" }) => {
  const [opened, setOpened] = useState(false);

  if (!instructions || instructions.length === 0) {
    return null;
  }

  return (
    <Paper withBorder>
      <Button
        variant="subtle"
        fullWidth
        onClick={() => setOpened(!opened)}
        style={{ 
          justifyContent: 'space-between',
          padding: '12px 16px',
          height: 'auto',
          fontWeight: 500,
          borderRadius: opened ? '4px 4px 0 0' : '4px'
        }}
      >
        <Group spacing="xs" align="center">
          <IconBook size={16} />
          <Text size="sm" color="dark" style={{ textAlign: 'left' }}>
            {title}
          </Text>
        </Group>
        {opened ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
      </Button>
      
      <Collapse in={opened}>
        <Box p="md" style={{ borderTop: opened ? '1px solid #e9ecef' : 'none' }}>
          <Stack spacing="md">
            {instructions.map((instruction, index) => (
              <InstructionItem key={index} instruction={instruction} />
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default Instructions; 